import { ErrorView } from "@/components/ui/layout/error/errorView"
import { LoadingSpinner } from "@/modules/ui/components/loading"
import { BoxStaffAddForm } from "@modules/boxStaff/components/boxStaffAddForm"
import { BoxStaffCardsView } from "@modules/boxStaff/components/boxStaffCardsView"
import { BoxStaffDetailsInfo } from "@modules/boxStaff/components/boxStaffDetailsInfo"
import { BoxStaffDetailsPending } from "@modules/boxStaff/components/boxStaffDetailsPending"
import { BoxStaffPermissions, BoxStaffRole } from "@modules/boxStaff/gateway/boxStaffDto"
import { useBoxStaffPermission } from "@modules/boxStaff/hooks/useBoxStaffPermissions"
import { MemberDetailsSkeleton } from "@modules/members/components/memberDetailsSkeleton"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { type SidebarNavigatorParams } from "@modules/sidebar/sidebarTypes"
import { Suspense } from "react"
import { ErrorBoundary } from "react-error-boundary"

export const useBoxStaffSidebar = () => {
  const { sidebarNavigateTo, hideSidebar } = useSidebar()
  const canAddStaff = useBoxStaffPermission(BoxStaffPermissions.ADD_STAFF)

  const navigateToBoxStaffCards = () =>
    void sidebarNavigateTo({
      content: (
        <ErrorBoundary fallback={<ErrorView />}>
          <Suspense fallback={<LoadingSpinner />}>
            <BoxStaffCardsView />
          </Suspense>
        </ErrorBoundary>
      ),
      replaceAllComponents: true,
    })

  const navigateToStaffDetailsInfo = (staffId: string, args: SidebarNavigatorParams) => {
    void sidebarNavigateTo({
      content: (
        <ErrorBoundary fallback={<ErrorView />}>
          <Suspense fallback={<LoadingSpinner />}>
            <BoxStaffDetailsInfo staffId={staffId} />
          </Suspense>
        </ErrorBoundary>
      ),
      ...args,
    })
  }

  const navigateToStaffDetailsPending = (staffId: string, args: SidebarNavigatorParams) => {
    void sidebarNavigateTo({
      content: (
        <ErrorBoundary fallback={<ErrorView />}>
          <Suspense fallback={<MemberDetailsSkeleton />}>
            <BoxStaffDetailsPending staffId={staffId} />
          </Suspense>
        </ErrorBoundary>
      ),
      ...args,
    })
  }

  const navigateToAddBoxStaffForm = (boxStaffRole: Exclude<BoxStaffRole, "READ_ONLY">) => {
    // PERMISSIONS - Head-coach && Coach & Financial permissions denied
    if (!canAddStaff) {
      return null
    }

    void sidebarNavigateTo({
      content: (
        <ErrorBoundary fallback={<ErrorView />}>
          <Suspense fallback={<LoadingSpinner />}>
            <BoxStaffAddForm boxStaffRole={boxStaffRole} />
          </Suspense>
        </ErrorBoundary>
      ),
      replaceAllComponents: true,
    })
  }

  return {
    navigateToBoxStaffCards,
    navigateToStaffDetailsInfo,
    navigateToStaffDetailsPending,
    hideSidebar,
    navigateToAddBoxStaffForm,
  }
}
